import { useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { getAuth } from '../slices/authSlice';
import logApi from '../api/logApi';

/**
 * Custom hook for logging user actions
 * Keeps track of actions in memory and sends them to the backend
 * 
 * @returns {Object} Object containing log methods
 * @returns {Function} logAction - Main function to log an action
 * @returns {Function} logPageView - Convenience function to log page views
 * @returns {Function} logSearch - Convenience function to log searches
 * @returns {Function} logError - Convenience function to log errors
 * @returns {Array} getLogs - Get all logs stored in memory
 * @returns {Function} clearLogs - Clear logs from memory
 */
const useLog = () => {
  const auth = useSelector(getAuth);
  const logsRef = useRef([]);
  
  // Generate a unique session ID when the hook is first initialized
  const sessionIdRef = useRef(null);
  if (!sessionIdRef.current) {
    sessionIdRef.current = `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  }

  /**
   * Main logging function
   * @param {string} action - Short description of the action (e.g., "login", "page-view", "search")
   * @param {Object} options - Additional log options
   * @param {Object} options.details - Detailed information about the action
   * @param {string} options.route - Route or page path
   * @param {string} options.method - HTTP method (GET, POST, etc.)
   * @param {number} options.statusCode - HTTP status code
   * @param {Object} options.meta - Additional metadata
   * @param {boolean} options.saveToMemory - Whether to save in memory (default: true)
   * @param {boolean} options.sendToApi - Whether to send to API (default: true)
   * @returns {Promise<Object|null>} Response from API or log object if only saved to memory
   */
  const logAction = useCallback(async (action, options = {}) => {
    const {
      details = null,
      route = window.location.pathname,
      method = null,
      statusCode = null,
      meta = null,
      saveToMemory = true,
      sendToApi = true,
    } = options;

    // Prepare log entry
    const logEntry = {
      user: auth.User?._id || null,
      action,
      details,
      ip: null, // IP will be captured by backend
      userAgent: navigator.userAgent,
      route,
      method,
      statusCode,
      meta,
      sessionId: sessionIdRef.current,
      createdAt: new Date().toISOString(),
    };

    // Save to memory if requested
    if (saveToMemory) {
      logsRef.current.push(logEntry);
      
      // Optional: Limit memory logs to prevent memory issues (keep last 100 logs)
      if (logsRef.current.length > 100) {
        logsRef.current.shift();
      }
    }

    // Send to API if requested
    if (sendToApi) {
      try {
        const response = await logApi.addLog(logEntry);
        return response;
      } catch (err) {
        console.error('Failed to send log to API:', err);
        return null;
      }
    }

    return logEntry;
  }, [auth.User]);

  /**
   * Log a page view
   * @param {string} pageName - Name of the page
   * @param {Object} additionalDetails - Additional details about the page view
   * @returns {Promise<Object|null>}
   */
  const logPageView = useCallback(async (pageName, additionalDetails = {}) => {
    return logAction('Page view', {
      details: {
        page: pageName,
        ...additionalDetails,
      },
      method: 'GET',
    });
  }, [logAction]);

  /**
   * Log a search action
   * @param {string} searchQuery - The search query
   * @param {Object} searchContext - Context about the search (e.g., filters, category)
   * @returns {Promise<Object|null>}
   */
  const logSearch = useCallback(async (searchQuery, searchContext = {}) => {
    return logAction('Search', {
      details: {
        query: searchQuery,
        ...searchContext,
      },
      method: 'GET',
    });
  }, [logAction]);

  /**
   * Log a product view
   * @param {string} productId - Product ID
   * @param {Object} productDetails - Additional product details
   * @returns {Promise<Object|null>}
   */
  const logProductView = useCallback(async (productId, productDetails = {}) => {
    return logAction('Product view', {
      details: {
        productId,
        ...productDetails,
      },
      method: 'GET',
    });
  }, [logAction]);

  /**
   * Log an error
   * @param {string} errorMessage - Error message
   * @param {Object} errorContext - Context about the error
   * @returns {Promise<Object|null>}
   */
  const logError = useCallback(async (errorMessage, errorContext = {}) => {
    return logAction('error', {
      details: {
        message: errorMessage,
        ...errorContext,
      },
      statusCode: errorContext.statusCode || 500,
    });
  }, [logAction]);

  /**
   * Log a user action (click, submit, etc.)
   * @param {string} actionType - Type of action (e.g., "button-click", "form-submit")
   * @param {Object} actionDetails - Details about the action
   * @returns {Promise<Object|null>}
   */
  const logUserAction = useCallback(async (actionType, actionDetails = {}) => {
    return logAction(actionType, {
      details: actionDetails,
    });
  }, [logAction]);

  /**
   * Get all logs from memory
   * @returns {Array} Array of log entries
   */
  const getLogs = useCallback(() => {
    return logsRef.current;
  }, []);

  /**
   * Clear logs from memory
   */
  const clearLogs = useCallback(() => {
    logsRef.current = [];
  }, []);

  /**
   * Get logs count
   * @returns {number} Number of logs in memory
   */
  const getLogsCount = useCallback(() => {
    return logsRef.current.length;
  }, []);

  /**
   * Get the current session ID
   * @returns {string} Current session ID
   */
  const getSessionId = useCallback(() => {
    return sessionIdRef.current;
  }, []);

  return {
    logAction,
    logPageView,
    logSearch,
    logProductView,
    logError,
    logUserAction,
    getLogs,
    clearLogs,
    getLogsCount,
    getSessionId,
  };
};

export default useLog;
